import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
} from "@react-pdf/renderer";
import moment from "moment";

// ─── Styles ────────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  page: {
    paddingTop: 145,
    paddingHorizontal: 24,
    paddingBottom: 24,
    fontSize: 9,
    fontFamily: "Helvetica",
    backgroundColor: "#fff",
  },

  /* ── HEADER (fixed) ── */
  header: {
    position: "absolute",
    top: 20,
    left: 24,
    right: 24,
    textAlign: "center",
  },
  title: {
    fontSize: 12,
    fontWeight: "bold",
    textDecoration: "underline",
    marginBottom: 4,
    letterSpacing: 1,
  },
  companyName: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#111827",
    marginTop: 4,
    letterSpacing: 1,
  },
  addressWrap: {
    marginTop: 8,
    paddingHorizontal: 20,
  },
  addressText: {
    fontSize: 9,
    lineHeight: 1.6,
    textAlign: "center",
    color: "#444444",
    marginTop: 3,
  },
  gstText: {
    fontSize: 9,
    marginTop: 5,
    textAlign: "center",
    fontWeight: "bold",
  },
  mobileText: {
    fontSize: 9,
    marginTop: 3,
    textAlign: "center",
  },
  divider: {
    borderBottomWidth: 2,
    borderBottomColor: "#000",
    marginTop: 12,
  },

  /* ── INFO ROW ── */
  infoRow: {
    flexDirection: "row",
    borderWidth: 1,
    borderBottomWidth: 0,
  },
  infoBox: {
    flex: 1,
    padding: 6,
    borderRightWidth: 1,
  },
  infoText: {
    fontSize: 9,
    fontWeight: "bold",
  },

  /* ── SUPPLIER ROW ── */
  supplierRow: {
    flexDirection: "row",
    borderWidth: 1,
  },
  supplierBox: {
    flex: 1,
    padding: 8,
    borderRightWidth: 1,
    minHeight: 10,
  },
  label: {
    fontWeight: "bold",
    marginBottom: 4,
    fontSize: 10,
  },
  supplierText: {
    marginBottom: 3,
    lineHeight: 1.4,
  },

  /* ── TABLE ── */
  table: {
    width: "100%",
    borderWidth: 1,
    borderTopWidth: 0,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f3f4f6",
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    alignItems: "center",
    minHeight: 28,
  },
  row: {
    flexDirection: "row",
    borderBottomWidth: 1,
    alignItems: "center",
    minHeight: 24,
  },
  rowAlt: {
    flexDirection: "row",
    borderBottomWidth: 1,
    alignItems: "center",
    minHeight: 24,
    backgroundColor: "#fafafa",
  },
  headerText: {
    fontWeight: "bold",
    fontSize: 8.5,
  },

  /* ── COLUMNS ── */
  sno: {
    width: "10%",
    padding: 4,
    borderRightWidth: 1,
    textAlign: "center",
  },
  product: {
    width: "70%",
    padding: 4,
    borderRightWidth: 1,
  },
  qty: {
    width: "20%",
    padding: 4,
    textAlign: "center",
  },

  /* ── FOOTER ── */
  footer: {
    marginTop: 20,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  terms: {
    width: "60%",
  },
  termsTitle: {
    fontWeight: "bold",
    marginBottom: 5,
    textDecoration: "underline",
  },
  termsText: {
    marginBottom: 3,
    lineHeight: 1.5,
  },
  signBox: {
    width: "35%",
    textAlign: "center",
  },
  signCompany: {
    fontWeight: "bold",
  },
  signText: {
    marginTop: 45,
    fontSize: 9,
    fontWeight: "bold",
  },
});

// ─── PDF Document ──────────────────────────────────────────────────────────────
const PurchasePDF = ({ order, items }) => {
  // If parent component hasn't fetched data yet, gracefully handle it
  if (!order || !items) return null;

  const TOTAL_ROWS = 20;
  const emptyCount = Math.max(0, TOTAL_ROWS - items.length);

  const isCeramic = Number(order.branch) === 2;
  const companyName = isCeramic ? "A.K. CERAMIC WORLD" : "A.K. TILES PARK";

  return (
    <Document>
      <Page size="A4" style={styles.page}>

        {/* ── FIXED HEADER ── */}
        <View fixed style={styles.header}>
          <Text style={styles.title}>PURCHASE ORDER</Text>
          <Text style={styles.companyName}>{companyName}</Text>
          <View style={styles.addressWrap}>
            <Text style={styles.addressText}>
              {isCeramic
                ? "70, Palayamkottai Taluk, V.G Panneer Das Town Bye Pass Road, Reddiyarpatti, Tirunelveli - 7"
                : "25-G/3, S.N. High Road, Tirunelveli Junction - 627001"}
            </Text>
            <Text style={styles.gstText}>
              {isCeramic ? "GSTIN : 33ACDFA2032P1ZL" : "GSTIN : 33BTZPK5150N1ZZ"}
            </Text>
            <Text style={styles.mobileText}>
              {isCeramic
                ? "Mobile : 97507 92020, 63746 07590"
                : "Mobile : 98653 72023, 97507 92020"}
            </Text>
          </View>
          <View style={styles.divider} />
        </View>

        {/* ── INFO ROW ── */}
        <View style={styles.infoRow}>
          <View style={styles.infoBox}>
            <Text style={styles.infoText}>
              Purchase No : PO-{order.purchase_id}
            </Text>
          </View>
          <View style={[styles.infoBox, { borderRightWidth: 0 }]}>
            <Text style={styles.infoText}>
              Date : {moment(order.created_at).format("DD/MM/YYYY")}
            </Text>
          </View>
        </View>

        {/* ── SUPPLIER ROW ── */}
        <View style={styles.supplierRow}>
          <View style={styles.supplierBox}>
            <Text style={styles.label}>SUPPLIER DETAILS</Text>
            <Text style={styles.supplierText}>Name    : {order.supplier_name}</Text>
            <Text style={styles.supplierText}>Mobile  : {order.phone}</Text>
            <Text style={styles.supplierText}>Address :</Text>
            <Text style={styles.supplierText}>{order.address || ""}</Text>
          </View>
          <View style={[styles.supplierBox, { borderRightWidth: 0 }]}>
            <Text style={styles.label}>PURCHASE NOTES</Text>
            <Text style={styles.supplierText}>• Stock will be verified before adding.</Text>
            <Text style={styles.supplierText}>• Quantity once received cannot be reverted.</Text>
            <Text style={styles.supplierText}>• Purchase for inventory purpose only.</Text>
          </View>
        </View>

        {/* ── TABLE ── */}
        <View style={styles.table}>

          {/* Header */}
          <View fixed style={styles.tableHeader}>
            <Text style={[styles.sno, styles.headerText]}>S.NO</Text>
            <Text style={[styles.product, styles.headerText]}>PRODUCT</Text>
            <Text style={[styles.qty, styles.headerText]}>QTY</Text>
          </View>

          {/* Item rows */}
          {items.map((item, index) => (
            <View
              key={index}
              style={index % 2 === 0 ? styles.row : styles.rowAlt}
              wrap={false}
            >
              <Text style={styles.sno}>{index + 1}</Text>
              <Text style={styles.product}>{item.product_name}</Text>
              <Text style={styles.qty}>{item.qty}</Text>
            </View>
          ))}

          {/* Empty filler rows */}
          {Array.from({ length: emptyCount }).map((_, i) => (
            <View
              key={`empty-${i}`}
              style={(items.length + i) % 2 === 0 ? styles.row : styles.rowAlt}
            >
              <Text style={styles.sno}> </Text>
              <Text style={styles.product}> </Text>
              <Text style={styles.qty}> </Text>
            </View>
          ))}

        </View>

        {/* ── FOOTER ── */}
        <View style={styles.footer}>
          <View style={styles.terms}>
            <Text style={styles.termsTitle}>Terms &amp; Conditions</Text>
            <Text style={styles.termsText}>1. Goods received will be checked physically.</Text>
            <Text style={styles.termsText}>2. Damaged products should be informed immediately.</Text>
            <Text style={styles.termsText}>3. Purchase order valid for inventory update only.</Text>
          </View>
          <View style={styles.signBox}>
            <Text style={styles.signCompany}>For {companyName}</Text>
            <Text style={styles.signText}>Authorised Signatory</Text>
          </View>
        </View>

      </Page>
    </Document>
  );
};

export default PurchasePDF;